import {
  InviteItem,
  WebDashboardShareInfo,
  WebDashboardShareRole,
  WebDashboardSharingLevel,
} from "@coeff/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";

import { AVATAR_COLORS } from "../../constants";
import { AppState } from "../../store";

import { MobileBottomSheet } from "./MobileBottomSheet";
import {
  useDashboardPermissions,
  useDashboardShares,
  useInviteDashboardShareMutation,
  usePatchDashboardSharingLevelMutation,
} from "./webDashboard";

interface ShareDashboardModalProps {
  dashboardId: string;
  open: boolean;
  onClose: () => void;
  requestSuperAdminView?: boolean;
  hasPersonalizedViews?: boolean;
  /** Called when the user clicks the Personalized Views toggle. Receives the prefill message for the chat. */
  onOpenPersonalizedViews?: (message: string) => void;
  /** Iframe document — enables the mobile bottom-sheet presentation and correct portal target. */
  targetDocument?: Document;
}

const SHARING_LEVEL_OPTIONS: {
  value: WebDashboardSharingLevel;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: "private",
    label: "Private",
    description: "Only people added can access",
    icon: "language",
  },
  {
    value: "domain",
    label: "Anyone at my domain",
    description: "Anyone with an email from one of your domains",
    icon: "apartment",
  },
  {
    value: "public",
    label: "Anyone with the link",
    description: "Anyone with the link can view (editors must be added)",
    icon: "public",
  },
];

const ROLE_OPTIONS: { value: WebDashboardShareRole; label: string }[] = [
  { value: "viewer", label: "Viewer" },
  { value: "editor", label: "Editor" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getAvatarColor = (index: number): string => AVATAR_COLORS[index % AVATAR_COLORS.length];

const getDisplayName = (email: string): string => {
  const local = email.split("@")[0] || "";
  return local.replace(/[._-]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Hook for dropdown menus that float as a fixed overlay.
 * Uses ownerDocument from the button ref so it works correctly inside iframes.
 */
const useFixedDropdown = (align: "left" | "right" = "right") => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties | undefined>(undefined);
  // The menu is portaled to the document body so it escapes the mobile share sheet's `transform`
  // (translate-y), which would otherwise become the containing block for the `position: fixed`
  // menu and offset it off-screen (making the dropdowns untappable on mobile).
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setStyle(undefined);
      setPortalTarget(null);
      return;
    }

    const button = buttonRef.current;
    const ownerDoc = button.ownerDocument;
    const ownerWin = ownerDoc.defaultView;
    if (!ownerWin) {
      return;
    }

    setPortalTarget(ownerDoc.body);
    const rect = button.getBoundingClientRect();
    setStyle({
      position: "fixed",
      top: rect.bottom + 4,
      ...(align === "right" ? { right: ownerWin.innerWidth - rect.right } : { left: rect.left }),
    });

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    ownerDoc.addEventListener("mousedown", handleClickOutside);
    return () => ownerDoc.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, align]);

  return { isOpen, toggle, close, style, buttonRef, menuRef, portalTarget };
};

const RoleDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  showRemove?: boolean;
  onRemove?: () => void;
}> = ({ value, onChange, options, disabled, showRemove, onRemove }) => {
  const { isOpen, toggle, close, style, buttonRef, menuRef, portalTarget } =
    useFixedDropdown("right");
  const selectedLabel = options.find(o => o.value === value)?.label || value;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={e => {
          e.stopPropagation();
          toggle();
        }}
        className="flex items-center gap-1 px-2 py-1 text-sm text-main-foreground hover:bg-main-text-button-hover-background rounded-md disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
      >
        <span>{selectedLabel}</span>
        <span className="material-icons !text-[18px] text-main-muted-foreground">
          arrow_drop_down
        </span>
      </button>

      {isOpen &&
        style &&
        portalTarget &&
        createPortal(
          <div
            ref={menuRef}
            className="bg-main-background border border-main-border rounded-lg shadow-card z-[400] min-w-[160px] py-1"
            style={style}
            onClick={e => e.stopPropagation()}
          >
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  close();
                }}
                className="w-full text-left px-3 py-2 text-sm text-main-foreground hover:bg-main-text-button-hover-background flex items-center justify-between cursor-pointer"
              >
                <span>{option.label}</span>
                {option.value === value && (
                  <span className="material-icons !text-base text-primary-button-background">
                    check
                  </span>
                )}
              </button>
            ))}
            {showRemove && (
              <>
                <div className="border-t border-main-border my-1" />
                <button
                  type="button"
                  onClick={() => {
                    onRemove?.();
                    close();
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-main-text-button-hover-background text-red-600 cursor-pointer"
                >
                  Remove access
                </button>
              </>
            )}
          </div>,
          portalTarget
        )}
    </>
  );
};

const SharingLevelDropdown: React.FC<{
  value: WebDashboardSharingLevel;
  onChange: (value: WebDashboardSharingLevel) => void;
  disabled?: boolean;
  disabledValues?: WebDashboardSharingLevel[];
  disabledTooltip?: string;
}> = ({ value, onChange, disabled, disabledValues = [], disabledTooltip }) => {
  const { isOpen, toggle, close, style, buttonRef, menuRef, portalTarget } =
    useFixedDropdown("left");
  const selected = SHARING_LEVEL_OPTIONS.find(o => o.value === value) || SHARING_LEVEL_OPTIONS[0];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={e => {
          e.stopPropagation();
          toggle();
        }}
        className="flex items-center gap-1 text-sm font-semibold text-main-foreground hover:text-main-text-button-hover-foreground disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        <span>{selected.label}</span>
        <span className="material-icons !text-[18px] text-main-muted-foreground">
          arrow_drop_down
        </span>
      </button>

      {isOpen &&
        style &&
        portalTarget &&
        createPortal(
          <div
            ref={menuRef}
            className="bg-main-background border border-main-border rounded-xl shadow-card z-[400] min-w-[220px] py-1.5"
            style={style}
            onClick={e => e.stopPropagation()}
          >
            {SHARING_LEVEL_OPTIONS.map(option => {
              const isOptionDisabled = disabledValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isOptionDisabled}
                  title={isOptionDisabled ? disabledTooltip : undefined}
                  onClick={() => {
                    if (!isOptionDisabled) {
                      onChange(option.value);
                      close();
                    }
                  }}
                  className={`w-full text-left px-4 py-2 text-sm ${
                    isOptionDisabled
                      ? "opacity-50 cursor-not-allowed text-main-foreground"
                      : `cursor-pointer hover:bg-main-text-button-hover-background ${
                          option.value === value
                            ? "text-primary-button-background font-medium"
                            : "text-main-foreground"
                        }`
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          portalTarget
        )}
    </>
  );
};

const DomainTooltip: React.FC<{ domains: string[] }> = ({ domains }) => {
  const [show, setShow] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const [style, setStyle] = useState<React.CSSProperties | undefined>(undefined);

  const hasPopover = domains.length > 1;

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const openTooltip = useCallback(() => {
    clearHideTimer();
    setShow(true);
  }, [clearHideTimer]);

  const scheduleClose = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setShow(false);
      hideTimerRef.current = null;
    }, 100);
  }, [clearHideTimer]);

  useEffect(() => clearHideTimer, [clearHideTimer]);

  useEffect(() => {
    if (!show || !anchorRef.current) {
      setStyle(undefined);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    setStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
    });
  }, [show]);

  if (domains.length === 0) {
    return null;
  }

  const label = domains.length === 1 ? domains[0] : "one of your domains";
  const anchorCursorClass = hasPopover ? "cursor-pointer" : "cursor-default";

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-block"
        onMouseEnter={openTooltip}
        onMouseLeave={scheduleClose}
      >
        <span className={`underline ${anchorCursorClass}`}>{label}</span>
      </span>
      {show && style && hasPopover && (
        <div
          className="z-[500] bg-main-background border border-main-border rounded-lg shadow-card px-4 py-3 min-w-[180px]"
          style={style}
          onMouseEnter={openTooltip}
          onMouseLeave={scheduleClose}
        >
          {domains.map(domain => (
            <div key={domain} className="text-sm text-main-foreground py-0.5">
              {domain}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const UserAvatar: React.FC<{ email: string; colorIndex: number }> = ({ email, colorIndex }) => (
  <div
    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
    style={{ backgroundColor: getAvatarColor(colorIndex) }}
  >
    {email.charAt(0).toUpperCase()}
  </div>
);

const EmailChip: React.FC<{ email: string; onRemove: () => void }> = ({ email, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 bg-main-text-button-hover-background rounded text-sm text-main-foreground">
    <span>{email}</span>
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onRemove();
      }}
      className="flex-shrink-0 text-main-muted-foreground hover:text-main-foreground cursor-pointer transition-colors text-base leading-none"
      aria-label={`Remove ${email}`}
    >
      &times;
    </button>
  </span>
);

export const ShareDashboardModal = React.forwardRef<HTMLDivElement, ShareDashboardModalProps>(
  (
    {
      dashboardId,
      open,
      onClose,
      requestSuperAdminView = false,
      hasPersonalizedViews = false,
      onOpenPersonalizedViews,
      targetDocument,
    },
    ref
  ) => {
    const [emailChips, setEmailChips] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [inviteRole, setInviteRole] = useState<WebDashboardShareRole>("viewer");
    const [generalAccessRole, setGeneralAccessRole] = useState<WebDashboardShareRole>("viewer");
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [pendingInviteEmails, setPendingInviteEmails] = useState<Set<string>>(new Set());
    const inputRef = useRef<HTMLInputElement>(null);

    const domains = useSelector((state: AppState) => state.app.domains_in_domain_family);

    const { data: permissions } = useDashboardPermissions(dashboardId, requestSuperAdminView);
    const { data: sharesData, isLoading: isLoadingShares } = useDashboardShares(
      open ? dashboardId : undefined,
      requestSuperAdminView
    );
    const inviteMutation = useInviteDashboardShareMutation(dashboardId);
    const patchSharingLevelMutation = usePatchDashboardSharingLevelMutation(dashboardId);

    const sharingLevel: WebDashboardSharingLevel = permissions?.sharing_level || "private";
    const canEdit = permissions?.can_edit || false;

    const shares: WebDashboardShareInfo[] = sharesData?.shares || [];

    const effectiveOwnerEmail = useMemo(() => {
      const ownerShare = shares.find(s => s.role === "owner");
      return ownerShare?.email || permissions?.owner_email || "";
    }, [shares, permissions?.owner_email]);

    const nonOwnerShares = useMemo(() => {
      return shares.filter(s => s.role !== "owner");
    }, [shares]);

    const addEmailChip = useCallback(
      (raw: string) => {
        setInviteError(null);
        const emails = raw.split(/[,;\s]+/).filter(Boolean);
        const valid: string[] = [];
        const invalid: string[] = [];

        for (const e of emails) {
          if (!EMAIL_REGEX.test(e)) {
            invalid.push(e);
          } else if (!emailChips.includes(e)) {
            valid.push(e);
          }
        }

        if (invalid.length > 0) {
          setInviteError(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`);
          return;
        }

        if (valid.length > 0) {
          setEmailChips(prev => [...prev, ...valid]);
        }
        setInputValue("");
      },
      [emailChips]
    );

    const removeEmailChip = useCallback((email: string) => {
      setEmailChips(prev => prev.filter(e => e !== email));
    }, []);

    const handleInvite = useCallback(async () => {
      setInviteError(null);

      let allEmails = [...emailChips];
      const trimmed = inputValue.trim();
      if (trimmed) {
        const extraEmails = trimmed.split(/[,;\s]+/).filter(Boolean);
        const invalidEmails = extraEmails.filter(e => !EMAIL_REGEX.test(e));
        if (invalidEmails.length > 0) {
          setInviteError(
            `Invalid email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`
          );
          return;
        }
        allEmails = [...allEmails, ...extraEmails.filter(e => !allEmails.includes(e))];
      }

      if (allEmails.length === 0) {
        return;
      }

      const invites: InviteItem[] = allEmails.map(email => ({
        email,
        role: inviteRole,
      }));

      setPendingInviteEmails(new Set(allEmails));
      setEmailChips([]);
      setInputValue("");

      try {
        await inviteMutation.mutateAsync(invites);
      } catch (err: any) {
        setInviteError(err?.response?.data?.message || err?.message || "Failed to invite");
      } finally {
        setPendingInviteEmails(new Set());
      }
    }, [emailChips, inputValue, inviteRole, inviteMutation]);

    const handleShareRoleChange = useCallback(
      async (email: string, newRole: WebDashboardShareRole) => {
        try {
          await inviteMutation.mutateAsync([{ email, role: newRole }]);
        } catch {
          // silently fail
        }
      },
      [inviteMutation]
    );

    const handleSharingLevelChange = useCallback(
      async (level: WebDashboardSharingLevel) => {
        try {
          await patchSharingLevelMutation.mutateAsync(level);
        } catch {
          // silently fail
        }
      },
      [patchSharingLevelMutation]
    );

    const handleCopyLink = useCallback(async () => {
      const dashboardLink = new URL(`/dashboards/${dashboardId}`, window.location.origin);
      try {
        await navigator.clipboard.writeText(dashboardLink.toString());
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch {
        // silently fail
      }
    }, [dashboardId]);

    const handleInputKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === "," || e.key === ";") {
          e.preventDefault();
          const trimmed = inputValue.trim().replace(/[,;]+$/, "");
          if (trimmed) {
            addEmailChip(trimmed);
          }
        } else if (e.key === "Backspace" && !inputValue && emailChips.length > 0) {
          setEmailChips(prev => prev.slice(0, -1));
        }
      },
      [inputValue, emailChips, addEmailChip]
    );

    const handleInputBlur = useCallback(() => {
      const trimmed = inputValue.trim().replace(/[,;]+$/, "");
      if (trimmed && EMAIL_REGEX.test(trimmed)) {
        addEmailChip(trimmed);
      }
    }, [inputValue, addEmailChip]);

    useEffect(() => {
      if (!open) {
        setEmailChips([]);
        setInputValue("");
        setInviteError(null);
        setCopySuccess(false);
        setPendingInviteEmails(new Set());
      }
    }, [open]);

    if (!open) {
      return null;
    }

    const generalAccessOption =
      SHARING_LEVEL_OPTIONS.find(o => o.value === sharingLevel) || SHARING_LEVEL_OPTIONS[0];

    const hasPendingInvites = pendingInviteEmails.size > 0;

    return (
      <MobileBottomSheet
        onClose={onClose}
        targetDocument={targetDocument}
        containerRef={ref}
        ariaLabel="Share dashboard"
        className="bg-main-background flex flex-col"
        desktopClassName="absolute top-full right-0 mt-2 z-[300] rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.2)] w-[500px] max-h-[80vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-2 flex-shrink-0">
          <h2 className="text-base font-semibold text-main-foreground">Share dashboard</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-main-muted-foreground hover:text-main-foreground rounded-full hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
            aria-label="Close"
          >
            <span className="material-icons !text-xl">close</span>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 min-h-0">
          {/* Invite section */}
          {canEdit && (
            <div className="px-6 pt-2 pb-4">
              <div className="flex items-stretch gap-2">
                <div
                  className="flex-1 relative border border-main-border rounded-lg bg-main-background focus-within:ring-2 focus-within:ring-primary-button-background focus-within:border-transparent min-h-[38px] cursor-text flex"
                  onClick={() => inputRef.current?.focus()}
                >
                  <div className="flex flex-1 flex-wrap items-center gap-1.5 px-2.5 py-1.5 pr-[100px] min-h-[38px]">
                    {emailChips.map(email => (
                      <EmailChip
                        key={email}
                        email={email}
                        onRemove={() => removeEmailChip(email)}
                      />
                    ))}
                    <div className="flex-1 flex items-center min-w-[80px] min-h-[22px] self-center">
                      <input
                        ref={inputRef}
                        type="text"
                        placeholder={emailChips.length === 0 ? "Add emails to invite" : "Add"}
                        value={inputValue}
                        onChange={e => {
                          setInputValue(e.target.value);
                          setInviteError(null);
                        }}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleInputBlur}
                        className="w-full border-0 bg-transparent p-0 text-sm leading-5 text-main-foreground placeholder:text-main-muted-foreground focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>
                  <div className="absolute top-1.5 right-1.5">
                    <RoleDropdown
                      value={inviteRole}
                      onChange={v => setInviteRole(v as WebDashboardShareRole)}
                      options={ROLE_OPTIONS}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleInvite}
                  disabled={
                    (emailChips.length === 0 && !inputValue.trim()) || inviteMutation.isLoading
                  }
                  className="px-5 h-[38px] text-sm font-medium text-primary-button-foreground bg-primary-button-background rounded-lg hover:bg-primary-button-hover-background disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors flex-shrink-0"
                >
                  {"Invite"}
                </button>
              </div>

              {inviteError && <p className="mt-1.5 text-xs text-red-600">{inviteError}</p>}
            </div>
          )}

          {/* General access */}
          <div className="px-6 pb-4">
            <h3 className="text-sm font-medium text-main-foreground mb-3">General access</h3>

            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-main-text-button-hover-background flex items-center justify-center flex-shrink-0">
                <span className="material-icons !text-xl text-main-muted-foreground">
                  {generalAccessOption.icon}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                {canEdit ? (
                  <SharingLevelDropdown
                    value={sharingLevel}
                    onChange={handleSharingLevelChange}
                    disabled={patchSharingLevelMutation.isLoading}
                    disabledValues={hasPersonalizedViews ? ["public"] : []}
                    disabledTooltip="Disabled — this dashboard uses Personalized Views"
                  />
                ) : (
                  <div className="text-sm font-semibold text-main-foreground">
                    {generalAccessOption.label}
                  </div>
                )}
                <div className="text-xs text-main-muted-foreground mt-0.5">
                  {sharingLevel === "domain" ? (
                    <>
                      Anyone with an email from <DomainTooltip domains={domains} />
                    </>
                  ) : (
                    generalAccessOption.description
                  )}
                </div>
              </div>
              {sharingLevel !== "private" && canEdit && (
                <RoleDropdown
                  value={
                    sharingLevel === "public" || sharingLevel === "domain"
                      ? "viewer"
                      : generalAccessRole
                  }
                  onChange={v => setGeneralAccessRole(v as WebDashboardShareRole)}
                  options={ROLE_OPTIONS}
                  disabled={sharingLevel === "public" || sharingLevel === "domain"}
                />
              )}
            </div>
          </div>

          {/* People with access */}
          <div className="px-6 pb-4">
            <h3 className="text-sm font-medium text-main-foreground mb-3">People with access</h3>

            <div className="space-y-3">
              {/* Owner */}
              <div className="flex items-center gap-3">
                <UserAvatar email={effectiveOwnerEmail} colorIndex={0} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-main-foreground truncate">
                    {getDisplayName(effectiveOwnerEmail)}
                  </div>
                  <div className="text-xs text-main-muted-foreground truncate">
                    {effectiveOwnerEmail}
                  </div>
                </div>
                <span className="text-sm text-main-muted-foreground">Owner</span>
              </div>

              {/* Shared users */}
              {isLoadingShares ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 rounded-full border-2 border-main-border border-t-primary-button-background animate-spin" />
                </div>
              ) : (
                nonOwnerShares.map((share, index) => (
                  <div key={share.web_dashboard_share_id} className="flex items-center gap-3">
                    <UserAvatar email={share.email} colorIndex={index + 1} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-main-foreground truncate">
                        {getDisplayName(share.email)}
                      </div>
                      <div className="text-xs text-main-muted-foreground truncate">
                        {share.email}
                      </div>
                    </div>
                    {pendingInviteEmails.has(share.email) ? (
                      <span className="text-sm text-main-muted-foreground italic">Inviting...</span>
                    ) : canEdit ? (
                      <RoleDropdown
                        value={share.role}
                        onChange={v =>
                          handleShareRoleChange(share.email, v as WebDashboardShareRole)
                        }
                        options={ROLE_OPTIONS}
                        showRemove
                        onRemove={() =>
                          handleShareRoleChange(share.email, WebDashboardShareRole.NoAccess)
                        }
                      />
                    ) : (
                      <span className="text-sm text-main-muted-foreground capitalize">
                        {share.role}
                      </span>
                    )}
                  </div>
                ))
              )}

              {/* Pending invites (not yet in shares list) */}
              {hasPendingInvites &&
                Array.from(pendingInviteEmails)
                  .filter(email => !shares.some(s => s.email === email))
                  .map((email, index) => (
                    <div key={email} className="flex items-center gap-3">
                      <UserAvatar email={email} colorIndex={nonOwnerShares.length + index + 1} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-main-foreground truncate">
                          {getDisplayName(email)}
                        </div>
                        <div className="text-xs text-main-muted-foreground truncate">{email}</div>
                      </div>
                      <span className="text-sm text-main-muted-foreground italic">Inviting...</span>
                    </div>
                  ))}
            </div>
          </div>
        </div>

        {/* Advanced settings — editors only */}
        {onOpenPersonalizedViews && canEdit && (
          <div className="px-6 pb-4 border-t border-main-border pt-4">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-main-muted-foreground hover:text-main-foreground transition-colors mb-3"
              onClick={() => setAdvancedOpen(v => !v)}
            >
              <span>Advanced settings</span>
              <span className="material-icons !text-base">
                {advancedOpen ? "expand_less" : "expand_more"}
              </span>
            </button>

            {advancedOpen && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const message = hasPersonalizedViews
                      ? "I want to remove Personalized Views from this dashboard."
                      : "I want to set up Personalized Views so each viewer only sees their own data.";
                    onClose();
                    onOpenPersonalizedViews(message);
                  }}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    hasPersonalizedViews ? "bg-primary-button-background" : "bg-main-foreground/20"
                  }`}
                  role="switch"
                  aria-checked={hasPersonalizedViews}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      hasPersonalizedViews ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-main-foreground">Personalized Views</div>
                  <div className="text-xs text-main-muted-foreground">
                    Automatically applies a filter for each viewer
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 flex-shrink-0 border-t border-main-border">
          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex h-9 items-center gap-2 px-4 text-sm font-medium text-primary-button-background bg-primary-button-background/10 hover:bg-primary-button-background/20 rounded-lg cursor-pointer transition-colors"
          >
            <span
              className="material-icons !text-lg !leading-none shrink-0 inline-flex items-center justify-center -rotate-[35deg]"
              aria-hidden
            >
              link
            </span>
            <span className="leading-none">{copySuccess ? "Copied!" : "Copy link"}</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center justify-center px-5 text-sm font-semibold text-primary-button-background bg-primary-button-background/10 hover:bg-primary-button-background/20 cursor-pointer rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </MobileBottomSheet>
    );
  }
);

ShareDashboardModal.displayName = "ShareDashboardModal";
